import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import QuoteCommentsPanel from '../components/QuoteCommentsPanel';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useDropzone } from 'react-dropzone';
import { Clock, CheckCircle2, XCircle, Upload, FileText, Image, AlertCircle, Settings, Search, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuoteWorkflowPage() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [inProgressDialogOpen, setInProgressDialogOpen] = useState(false);
  const [cancelledDialogOpen, setCancelledDialogOpen] = useState(false);
  const [jobsheetDialogOpen, setJobsheetDialogOpen] = useState(false);
  const [adminStatusDialogOpen, setAdminStatusDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [workCompletedDialogOpen, setWorkCompletedDialogOpen] = useState(false);
  
  // Export filter states
  const [supervisorsList, setSupervisorsList] = useState([]);
  const [exportSupervisor, setExportSupervisor] = useState('all');
  const [exportStatus, setExportStatus] = useState('all');
  const [exporting, setExporting] = useState(false);
  
  // Form states
  const [workCommencementDate, setWorkCommencementDate] = useState('');
  const [workCompletionDate, setWorkCompletionDate] = useState('');
  const [cancelRemarks, setCancelRemarks] = useState('');
  const [jobsheetFile, setJobsheetFile] = useState(null);
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [afterPhoto, setAfterPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [adminNewStatus, setAdminNewStatus] = useState('');
  const [adminRemarks, setAdminRemarks] = useState('');
  
  const isSuperAdmin = user?.roles?.includes('Super Admin');
  const isSiteSupervisor = user?.roles?.includes('Site Supervisor') && !isSuperAdmin;

  useEffect(() => {
    fetchQuotes();
  }, []);

  // Filter quotes based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = quotes.filter(
        (q) =>
          q.quote_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.supervisor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.work_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.store_address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredQuotes(filtered);
    } else {
      setFilteredQuotes(quotes);
    }
  }, [searchTerm, quotes]);

  const fetchQuotes = async () => {
    try {
      // Get all quotes for Super Admin to manage
      const allQuotesResponse = await axios.get(`${API}/quotes`);
      
      if (isSuperAdmin) {
        // Super Admin sees all quotes for status management
        setQuotes(allQuotesResponse.data);
        setFilteredQuotes(allQuotesResponse.data);
      } else {
        // Regular users see quotes requiring action
        // Get quotes with paid fund requests or approved materials (these have has_requests flag)
        const actionResponse = await axios.get(`${API}/quotes/supervisor-action`);
        
        // Create a map from action quotes (these have has_requests: true)
        const actionQuotesMap = new Map();
        actionResponse.data.forEach(q => actionQuotesMap.set(q.id, q));
        
        // Get additional jobsheet quotes that might not be in action response
        // (quotes with jobsheet_pending/billing_rejected but no paid funds or approved materials)
        const additionalJobsheetQuotes = allQuotesResponse.data.filter(q => 
          (q.work_status === 'jobsheet_pending' || q.work_status === 'billing_rejected') &&
          !actionQuotesMap.has(q.id)
        );
        
        // Combine: prioritize action quotes (with has_requests flag), then add additional jobsheet quotes
        const combined = [...actionResponse.data, ...additionalJobsheetQuotes];
        
        setQuotes(combined);
        setFilteredQuotes(combined);
      }
    } catch (error) {
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  // Fetch supervisors list for export filter
  const fetchSupervisors = async () => {
    try {
      const response = await axios.get(`${API}/quotes/supervisors`);
      setSupervisorsList(response.data);
    } catch (error) {
      console.error('Failed to fetch supervisors', error);
    }
  };

  // Open export dialog
  const openExportDialog = () => {
    // For Site Supervisors, don't fetch all supervisors - they can only export their own data
    if (!isSiteSupervisor) {
      fetchSupervisors();
      setExportSupervisor('all');
    } else {
      // Site Supervisor: pre-fill with their name
      setExportSupervisor(user?.name || '');
    }
    setExportStatus('all');
    setExportDialogOpen(true);
  };

  // Handle export to Excel
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportSupervisor !== 'all') params.append('supervisor', exportSupervisor);
      if (exportStatus !== 'all') params.append('status', exportStatus);
      
      const response = await axios.get(`${API}/quotes/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quotes_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
      setExportDialogOpen(false);
    } catch (error) {
      toast.error('Failed to export quotes');
    } finally {
      setExporting(false);
    }
  };

  const handleWorkInProgress = async () => {
    if (!workCommencementDate) {
      toast.error('Work Commencement Date is mandatory');
      return;
    }
    
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('work_commencement_date', workCommencementDate);
      
      await axios.post(`${API}/quotes/${selectedQuote.id}/work-in-progress`, formData);
      toast.success('Work status updated to In Progress');
      setInProgressDialogOpen(false);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWorkCancelled = async () => {
    if (!cancelRemarks.trim()) {
      toast.error('Remarks are mandatory');
      return;
    }
    if (cancelRemarks.length > 500) {
      toast.error('Remarks must be 500 characters or less');
      return;
    }
    
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('remarks', cancelRemarks);
      
      await axios.post(`${API}/quotes/${selectedQuote.id}/work-cancelled`, formData);
      toast.success('Work cancelled');
      setCancelledDialogOpen(false);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel work');
    } finally {
      setSubmitting(false);
    }
  };

  const openWorkCompletedDialog = (quote) => {
    setSelectedQuote(quote);
    setWorkCompletionDate(new Date().toISOString().split('T')[0]);
    setWorkCompletedDialogOpen(true);
  };

  const handleWorkCompleted = async () => {
    if (!workCompletionDate) {
      toast.error('Work completion date is required');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes/${selectedQuote.id}/work-completed`, {
        completion_date: workCompletionDate
      });
      toast.success('Work completed. Please upload Job Sheet and photos.');
      setWorkCompletedDialogOpen(false);
      setSelectedQuote(null);
      setWorkCompletionDate('');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete work');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadJobsheet = async () => {
    // Check if this is a re-upload for rejected documents
    const isReupload = selectedQuote?.work_status === 'billing_rejected';
    
    if (isReupload) {
      // For re-upload, only check required rejected documents
      const needsJobsheet = selectedQuote?.jobsheet_rejection_remarks;
      const needsBeforePhoto = selectedQuote?.before_photo_rejection_remarks;
      const needsAfterPhoto = selectedQuote?.after_photo_rejection_remarks;
      
      if ((needsJobsheet && !jobsheetFile) || 
          (needsBeforePhoto && !beforePhoto) || 
          (needsAfterPhoto && !afterPhoto)) {
        toast.error('Please upload all rejected documents');
        return;
      }
      
      setSubmitting(true);
      try {
        const formData = new FormData();
        if (needsJobsheet && jobsheetFile) formData.append('jobsheet', jobsheetFile);
        if (needsBeforePhoto && beforePhoto) formData.append('before_photo', beforePhoto);
        if (needsAfterPhoto && afterPhoto) formData.append('after_photo', afterPhoto);
        
        await axios.post(`${API}/quotes/${selectedQuote.id}/reupload-rejected`, formData);
        toast.success('Rejected documents re-uploaded successfully');
        setJobsheetDialogOpen(false);
        setJobsheetFile(null);
        setBeforePhoto(null);
        setAfterPhoto(null);
        fetchQuotes();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to upload files');
      } finally {
        setSubmitting(false);
      }
    } else {
      // Original upload - all files required
      if (!jobsheetFile || !beforePhoto || !afterPhoto) {
        toast.error('All three files (Job Sheet, Before Photo, After Photo) are mandatory');
        return;
      }
      
      setSubmitting(true);
      try {
        const formData = new FormData();
        formData.append('jobsheet', jobsheetFile);
        formData.append('before_photo', beforePhoto);
        formData.append('after_photo', afterPhoto);
        
        await axios.post(`${API}/quotes/${selectedQuote.id}/upload-jobsheet`, formData);
        toast.success('Jobsheet and photos uploaded successfully');
        setJobsheetDialogOpen(false);
        setJobsheetFile(null);
        setBeforePhoto(null);
        setAfterPhoto(null);
        fetchQuotes();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to upload files');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const openInProgressDialog = (quote) => {
    setSelectedQuote(quote);
    setWorkCommencementDate(new Date().toISOString().split('T')[0]);
    setInProgressDialogOpen(true);
  };

  const openCancelledDialog = (quote) => {
    setSelectedQuote(quote);
    setCancelRemarks('');
    setCancelledDialogOpen(true);
  };

  const openJobsheetDialog = (quote) => {
    setSelectedQuote(quote);
    setJobsheetFile(null);
    setBeforePhoto(null);
    setAfterPhoto(null);
    setJobsheetDialogOpen(true);
  };

  const openAdminStatusDialog = (quote) => {
    setSelectedQuote(quote);
    setAdminNewStatus('');
    setAdminRemarks('');
    setAdminStatusDialogOpen(true);
  };

  const handleAdminStatusChange = async () => {
    if (!adminNewStatus) {
      toast.error('Please select a new status');
      return;
    }
    if (adminNewStatus === 'work_cancelled' && !adminRemarks.trim()) {
      toast.error('Remarks are required for cancellation');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/quotes/${selectedQuote.id}/change-work-status`, {
        new_status: adminNewStatus,
        remarks: adminRemarks || null
      });
      toast.success(`Work status changed to ${adminNewStatus.replace(/_/g, ' ')}`);
      setAdminStatusDialogOpen(false);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change status');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status, quote = null) => {
    // Check if quote has no requests initiated - show "Yet to initiate"
    if (quote && !quote.has_requests && (!status || status === 'pending')) {
      return (
        <Badge className="bg-gray-100 text-gray-800">
          <AlertCircle className="h-3 w-3 mr-1" />
          Yet to initiate
        </Badge>
      );
    }
    
    const variants = {
      pending: { className: 'bg-yellow-100 text-yellow-800', label: 'Ready for Work', icon: Clock },
      work_in_progress: { className: 'bg-blue-100 text-blue-800', label: 'Work In Progress', icon: Clock },
      work_completed: { className: 'bg-orange-100 text-orange-800', label: 'Jobsheet Pending', icon: Upload },  // Same as jobsheet_pending
      work_cancelled: { className: 'bg-red-100 text-red-800', label: 'Cancelled', icon: XCircle },
      jobsheet_pending: { className: 'bg-orange-100 text-orange-800', label: 'Jobsheet Pending', icon: Upload },
      jobsheet_submitted: { className: 'bg-purple-100 text-purple-800', label: 'Jobsheet Submitted', icon: FileText },
      billing_approved: { className: 'bg-green-100 text-green-800', label: 'Completed', icon: CheckCircle2 },
      billing_rejected: { className: 'bg-red-100 text-red-800', label: 'Jobsheet Rejected', icon: XCircle }
    };
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // File drop components with preview
  const FileDropZone = ({ file, setFile, label, accept, highlighted }) => {
    const [preview, setPreview] = useState(null);
    
    const onDrop = useCallback(acceptedFiles => {
      const f = acceptedFiles[0];
      if (f) {
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`${label}: File size must be less than 5MB`);
          return;
        }
        setFile(f);
        
        // Create preview URL for images
        if (f.type.startsWith('image/')) {
          const url = URL.createObjectURL(f);
          setPreview(url);
        } else if (f.type === 'application/pdf') {
          setPreview('pdf');
        }
      }
    }, [setFile, label]);

    // Cleanup preview URL on unmount
    useEffect(() => {
      return () => {
        if (preview && preview !== 'pdf') {
          URL.revokeObjectURL(preview);
        }
      };
    }, [preview]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      accept: {
        'application/pdf': ['.pdf'],
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/png': ['.png']
      },
      maxFiles: 1
    });

    const removeFile = (e) => {
      e.stopPropagation();
      setFile(null);
      if (preview && preview !== 'pdf') {
        URL.revokeObjectURL(preview);
      }
      setPreview(null);
    };

    return (
      <div className="space-y-2">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
            isDragActive ? 'border-[#DC143C] bg-red-50' : highlighted ? 'border-red-400 bg-red-50 hover:border-red-500' : 'border-gray-300 hover:border-[#DC143C]'
          } ${file ? 'bg-green-50 border-green-300' : ''}`}
        >
          <input {...getInputProps()} />
          {file ? (
            <div className="flex items-center justify-between gap-2 text-green-700">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-sm font-medium truncate max-w-[200px]">{file.name}</span>
              </div>
              <button
                onClick={removeFile}
                className="p-1 hover:bg-red-100 rounded-full text-red-500"
                type="button"
              >
                <XCircle className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div>
              <Upload className="h-6 w-6 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">{label}</p>
              <p className="text-xs text-gray-400 mt-1">PDF, JPEG, PNG (Max 5MB)</p>
            </div>
          )}
        </div>
        
        {/* Preview Section */}
        {file && preview && (
          <div className="border rounded-lg overflow-hidden bg-gray-50">
            {preview === 'pdf' ? (
              <div className="p-3 flex items-center gap-2 text-gray-700">
                <FileText className="h-5 w-5 text-red-500" />
                <span className="text-sm">PDF Document: {file.name}</span>
              </div>
            ) : (
              <div className="relative">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full h-32 object-contain bg-white"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-2 py-1">
                  {file.name}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-lg">Loading quotes...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="space-y-6" data-testid="quote-workflow-page">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Quote Workflow
            </h1>
            <p className="text-muted-foreground mt-2">
              {filteredQuotes.length} quote(s) found
            </p>
          </div>
          <Button
            onClick={openExportDialog}
            className="bg-green-600 hover:bg-green-700"
            data-testid="export-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
        </div>

        {/* Search */}
        <Card className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by quote number, supervisor, description, or store..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </Card>

        {filteredQuotes.length === 0 ? (
          <Card className="p-12 text-center glass-card">
            <p className="text-muted-foreground">No quotes found</p>
          </Card>
        ) : (
          <Card className="overflow-hidden glass-table">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="quote-workflow-table">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-4 text-left text-sm font-medium">Quote No</th>
                    <th className="px-4 py-4 text-left text-sm font-medium">Date</th>
                    <th className="px-4 py-4 text-left text-sm font-medium">Supervisor</th>
                    <th className="px-4 py-4 text-left text-sm font-medium">Work Description</th>
                    <th className="px-4 py-4 text-left text-sm font-medium">Store</th>
                    <th className="px-4 py-4 text-right text-sm font-medium">Amount</th>
                    <th className="px-4 py-4 text-left text-sm font-medium">Status</th>
                    <th className="px-4 py-4 text-center text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredQuotes.map((quote) => (
                    <tr key={quote.id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-4 py-4">
                        <span className="font-mono text-sm font-medium whitespace-nowrap">{quote.quote_no}</span>
                      </td>
                      <td className="px-4 py-4 text-sm whitespace-nowrap">
                        {quote.quote_date ? new Date(quote.quote_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span className="break-words">{quote.supervisor_name}</span>
                      </td>
                      <td className="px-4 py-4 text-sm max-w-[200px]">
                        <span className="break-words line-clamp-2">{quote.work_description}</span>
                      </td>
                      <td className="px-4 py-4 text-sm max-w-[200px]">
                        <span className="break-words line-clamp-2">{quote.store_address || '-'}</span>
                      </td>
                      <td className="px-4 py-4 text-sm text-right font-mono whitespace-nowrap">
                        <span className="inline-flex items-center">
                          ₹{quote.amount_with_management_fee?.toLocaleString('en-IN')}
                          {/* Hide comments panel (eye icon) for Site Supervisors */}
                          {!isSiteSupervisor && (
                            <QuoteCommentsPanel quoteId={quote.id} quoteNo={quote.quote_no} />
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {getStatusBadge(quote.work_status, quote)}
                        {/* Show rejection indicator */}
                        {quote.work_status === 'billing_rejected' && (
                          <div className="mt-1">
                            <span className="text-xs text-red-600 cursor-help" title={`${quote.jobsheet_rejection_remarks || ''} ${quote.before_photo_rejection_remarks || ''} ${quote.after_photo_rejection_remarks || ''}`}>
                              (Has rejection remarks)
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1 justify-center">
                          {/* Show action buttons based on status */}
                          {/* Disable actions for "Yet to initiate" quotes (no requests) */}
                          {(!quote.work_status || quote.work_status === 'pending') && (
                            quote.has_requests ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-blue-500 text-blue-600 hover:bg-blue-50 text-xs px-2"
                                  onClick={() => openInProgressDialog(quote)}
                                >
                                  Work In Progress
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-500 text-red-600 hover:bg-red-50 text-xs px-2"
                                  onClick={() => openCancelledDialog(quote)}
                                >
                                  Work Cancelled
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-xs px-2"
                                  onClick={() => openWorkCompletedDialog(quote)}
                                >
                                  Work Completed
                                </Button>
                              </>
                            ) : (
                              <span className="text-xs text-gray-500 italic">
                                No requests initiated
                              </span>
                            )
                          )}
                          
                          {(quote.work_status === 'jobsheet_pending' || quote.work_status === 'billing_rejected') && (
                            <Button
                              size="sm"
                              className="bg-[#DC143C] hover:bg-[#b91130] text-xs px-2"
                              onClick={() => openJobsheetDialog(quote)}
                            >
                              <Upload className="h-3 w-3 mr-1" />
                              Upload Jobsheet
                            </Button>
                          )}
                          
                          {quote.work_status === 'jobsheet_submitted' && (
                            <span className="text-xs text-purple-600">Awaiting Review</span>
                          )}
                          
                          {quote.work_status === 'billing_approved' && (
                            <span className="text-xs text-green-600">✓ Completed</span>
                          )}
                          
                          {quote.work_status === 'work_in_progress' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-xs px-2"
                                onClick={() => openWorkCompletedDialog(quote)}
                              >
                                Work Completed
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-500 text-red-600 hover:bg-red-50 text-xs px-2"
                                onClick={() => openCancelledDialog(quote)}
                              >
                                Work Cancelled
                              </Button>
                            </>
                          )}
                          
                          {quote.work_status === 'work_completed' && (
                            <Button
                              size="sm"
                              className="bg-[#DC143C] hover:bg-[#b91130] text-xs px-2"
                              onClick={() => openJobsheetDialog(quote)}
                            >
                              <Upload className="h-3 w-3 mr-1" />
                              Upload Jobsheet
                            </Button>
                          )}
                          
                          {quote.work_status === 'work_cancelled' && (
                            <span className="text-xs text-red-600">Cancelled</span>
                          )}
                          
                          {/* Super Admin: Change Status Button - also disabled for Yet to initiate */}
                          {isSuperAdmin && quote.has_requests && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-gray-500 text-gray-600 hover:bg-gray-50 text-xs px-2"
                              onClick={() => openAdminStatusDialog(quote)}
                              data-testid={`admin-change-status-${quote.id}`}
                            >
                              <Settings className="h-3 w-3 mr-1" />
                              Change Status
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Work In Progress Dialog */}
      <Dialog open={inProgressDialogOpen} onOpenChange={setInProgressDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set Work In Progress</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Quote: <span className="font-medium">{selectedQuote?.quote_no}</span></p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="commencement_date">Work Commencement Date *</Label>
              <Input
                id="commencement_date"
                type="date"
                value={workCommencementDate}
                onChange={(e) => setWorkCommencementDate(e.target.value)}
                required
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handleWorkInProgress}
                disabled={submitting || !workCommencementDate}
                className="flex-1"
              >
                {submitting ? 'Saving...' : 'Confirm'}
              </Button>
              <Button variant="outline" onClick={() => setInProgressDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Work Cancelled Dialog */}
      <Dialog open={cancelledDialogOpen} onOpenChange={setCancelledDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Work</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Quote: <span className="font-medium">{selectedQuote?.quote_no}</span></p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cancel_remarks">
                Remarks * <span className="text-xs text-muted-foreground">({cancelRemarks.length}/500)</span>
              </Label>
              <Textarea
                id="cancel_remarks"
                value={cancelRemarks}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setCancelRemarks(e.target.value);
                  }
                }}
                maxLength={500}
                rows={4}
                placeholder="Enter reason for cancellation"
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handleWorkCancelled}
                disabled={submitting || !cancelRemarks.trim()}
                variant="destructive"
                className="flex-1"
              >
                {submitting ? 'Saving...' : 'Confirm Cancellation'}
              </Button>
              <Button variant="outline" onClick={() => setCancelledDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Jobsheet Dialog */}
      <Dialog open={jobsheetDialogOpen} onOpenChange={setJobsheetDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedQuote?.work_status === 'billing_rejected' 
                ? 'Re-upload Rejected Documents' 
                : 'Upload Jobsheet & Photos'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Quote: <span className="font-medium">{selectedQuote?.quote_no}</span></p>
            </div>
            
            {/* For rejected quotes, show only rejected document fields */}
            {selectedQuote?.work_status === 'billing_rejected' ? (
              <div className="space-y-3">
                {selectedQuote?.jobsheet_rejection_remarks && (
                  <div>
                    <p className="text-xs text-red-600 mb-1 font-medium">Rejection: {selectedQuote.jobsheet_rejection_remarks}</p>
                    <FileDropZone
                      file={jobsheetFile}
                      setFile={setJobsheetFile}
                      label="Drop Job Sheet here or click to select"
                      highlighted={true}
                    />
                  </div>
                )}
                {selectedQuote?.before_photo_rejection_remarks && (
                  <div>
                    <p className="text-xs text-red-600 mb-1 font-medium">Rejection: {selectedQuote.before_photo_rejection_remarks}</p>
                    <FileDropZone
                      file={beforePhoto}
                      setFile={setBeforePhoto}
                      label="Drop Before Photo here or click to select"
                      highlighted={true}
                    />
                  </div>
                )}
                {selectedQuote?.after_photo_rejection_remarks && (
                  <div>
                    <p className="text-xs text-red-600 mb-1 font-medium">Rejection: {selectedQuote.after_photo_rejection_remarks}</p>
                    <FileDropZone
                      file={afterPhoto}
                      setFile={setAfterPhoto}
                      label="Drop After Photo here or click to select"
                      highlighted={true}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <FileDropZone
                  file={jobsheetFile}
                  setFile={setJobsheetFile}
                  label="Drop Job Sheet here or click to select"
                />
                <FileDropZone
                  file={beforePhoto}
                  setFile={setBeforePhoto}
                  label="Drop Before Photo here or click to select"
                />
                <FileDropZone
                  file={afterPhoto}
                  setFile={setAfterPhoto}
                  label="Drop After Photo here or click to select"
                />
              </div>
            )}
            
            <div className="flex gap-2">
              <Button
                onClick={handleUploadJobsheet}
                disabled={submitting || (
                  selectedQuote?.work_status === 'billing_rejected'
                    ? (
                        (selectedQuote?.jobsheet_rejection_remarks && !jobsheetFile) ||
                        (selectedQuote?.before_photo_rejection_remarks && !beforePhoto) ||
                        (selectedQuote?.after_photo_rejection_remarks && !afterPhoto)
                      )
                    : (!jobsheetFile || !beforePhoto || !afterPhoto)
                )}
                className="flex-1 bg-[#DC143C] hover:bg-[#b91130]"
              >
                {submitting ? 'Uploading...' : (selectedQuote?.work_status === 'billing_rejected' ? 'Re-upload Documents' : 'Upload All Files')}
              </Button>
              <Button variant="outline" onClick={() => setJobsheetDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Super Admin: Change Work Status Dialog */}
      <Dialog open={adminStatusDialogOpen} onOpenChange={setAdminStatusDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Change Work Status (Admin)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Quote: <span className="font-medium">{selectedQuote?.quote_no}</span></p>
              <p className="text-sm text-gray-500">Current Status: <span className="font-medium">{selectedQuote?.work_status?.replace(/_/g, ' ')}</span></p>
            </div>
            
            <div className="space-y-2">
              <Label>New Status *</Label>
              <Select value={adminNewStatus} onValueChange={setAdminNewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="work_in_progress">Work In Progress</SelectItem>
                  <SelectItem value="work_cancelled">Work Cancelled</SelectItem>
                  <SelectItem value="work_completed">Work Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {adminNewStatus === 'work_cancelled' && (
              <div className="space-y-2">
                <Label htmlFor="admin_remarks">Remarks * (Required for cancellation)</Label>
                <Textarea
                  id="admin_remarks"
                  placeholder="Enter reason for cancellation..."
                  value={adminRemarks}
                  onChange={(e) => setAdminRemarks(e.target.value)}
                  maxLength={500}
                />
                <p className="text-xs text-gray-500 text-right">{adminRemarks.length}/500</p>
              </div>
            )}
            
            {adminNewStatus === 'work_completed' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <AlertCircle className="h-4 w-4 inline mr-1" />
                  Setting to "Work Completed" will close all pending Fund & Material Requests for this quote.
                </p>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button
                onClick={handleAdminStatusChange}
                disabled={submitting || !adminNewStatus || (adminNewStatus === 'work_cancelled' && !adminRemarks.trim())}
                className="flex-1"
              >
                {submitting ? 'Updating...' : 'Update Status'}
              </Button>
              <Button variant="outline" onClick={() => setAdminStatusDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Export Quotes to Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Supervisor Filter - Hidden for Site Supervisors */}
            {!isSiteSupervisor ? (
              <div className="space-y-2">
                <Label htmlFor="export_supervisor">Supervisor</Label>
                <Select value={exportSupervisor} onValueChange={setExportSupervisor}>
                  <SelectTrigger id="export_supervisor">
                    <SelectValue placeholder="Select Supervisor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Supervisors</SelectItem>
                    {supervisorsList.map((supervisor) => (
                      <SelectItem key={supervisor} value={supervisor}>
                        {supervisor}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Supervisor</Label>
                <div className="p-2 bg-gray-100 rounded text-sm">
                  {user?.name} (Your data only)
                </div>
              </div>
            )}

            {/* Status Filter */}
            <div className="space-y-2">
              <Label htmlFor="export_status">Status</Label>
              <Select value={exportStatus} onValueChange={setExportStatus}>
                <SelectTrigger id="export_status">
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="yet_to_initiate">Yet to initiate</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="work_in_progress">Work In Progress</SelectItem>
                  <SelectItem value="work_cancelled">Cancelled</SelectItem>
                  <SelectItem value="jobsheet_pending">Jobsheet Pending</SelectItem>
                  <SelectItem value="jobsheet_submitted">Jobsheet Submitted</SelectItem>
                  <SelectItem value="billing_approved">Completed</SelectItem>
                  <SelectItem value="billing_rejected">Jobsheet Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleExport}
                disabled={exporting}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Download className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export'}
              </Button>
              <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Work Completed Dialog */}
      <Dialog open={workCompletedDialogOpen} onOpenChange={setWorkCompletedDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Mark Work as Completed
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Quote: <span className="font-medium">{selectedQuote?.quote_no}</span></p>
              <p className="text-sm text-gray-600">Store: <span className="font-medium">{selectedQuote?.store_name}</span></p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="work_completion_date">Work Completion Date *</Label>
              <Input
                id="work_completion_date"
                type="date"
                value={workCompletionDate}
                onChange={(e) => setWorkCompletionDate(e.target.value)}
                required
                data-testid="work-completion-date-input"
              />
            </div>

            <p className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg">
              After marking as completed, you can upload the job sheet and photos.
            </p>

            <div className="flex gap-2">
              <Button
                onClick={handleWorkCompleted}
                disabled={submitting || !workCompletionDate}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="confirm-work-completed-button"
              >
                {submitting ? 'Processing...' : 'Confirm Completed'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setWorkCompletedDialogOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
